// src/components/ActionItemCard.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { formatDate, formatScore } from '../utils';
import { SingleDatePicker } from './FilterControls';
import { ScoreTooltip } from './ScoreTooltips';
import { useStaffContext } from '../contexts/StaffContext';

const ActionItemCard = ({ 
    actionItem, 
    clientId, 
    onClick,
    editable = false,
    statusOptions = [],
    assigneeOptions = [],
    onStatusChange,
    onDeadlineChange,
    onAssigneeChange,
    'data-testid': dataTestId
}) => {
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const statusRef = useRef(null);
    const assigneeRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
            if (assigneeRef.current && !assigneeRef.current.contains(event.target)) {
                setShowAssigneeDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const handleClick = (e) => {
        if (e.target.closest('.editable-control')) return;
        
        if (onClick) {
            onClick(actionItem);
        } else if (actionItem.id && clientId) {
            navigate(`${ROUTES.ACCOUNTS}/${clientId}/action-items/${actionItem.id}`);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (onStatusChange && newStatus !== actionItem.status) {
            setIsUpdating(true);
            try {
                await onStatusChange(actionItem.id, newStatus);
            } finally {
                setIsUpdating(false);
                setShowStatusDropdown(false);
            }
        } else {
            setShowStatusDropdown(false);
        }
    };

    const handleAssigneeChange = async (newAssignee) => {
        if (onAssigneeChange) {
            setIsUpdating(true);
            try {
                await onAssigneeChange(actionItem.id, newAssignee);
            } finally {
                setIsUpdating(false);
                setShowAssigneeDropdown(false);
            }
        } else {
            setShowAssigneeDropdown(false);
        }
    };

    // Truncate description to a snippet
    const truncateText = (text, maxLength = 150) => {
        if (!text) return null;
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
            case 'pending':
                return { bg: 'rgba(0, 156, 220, 0.1)', color: 'var(--pallium-info-cyan)' };
            case 'in progress':
            case 'in-progress':
                return { bg: 'rgba(110, 79, 196, 0.1)', color: 'var(--pallium-primary-purple)' };
            case 'on hold':
            case 'on-hold':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'completed':
            case 'done':
            case 'closed':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    // Check if deadline is past
    const isOverdue = () => {
        if (!actionItem.deadline) return false;
        const deadline = new Date(actionItem.deadline);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return deadline < today && actionItem.status?.toLowerCase() !== 'completed' && actionItem.status?.toLowerCase() !== 'done';
    };

    const statusStyle = getStatusColor(actionItem.status);
    const overdue = isOverdue();
    const rawAssignee = actionItem.assignee || actionItem.assigned_to;
    const assigneeDisplayName = getDriDisplayName(rawAssignee);

    return (
        <div
            onClick={handleClick}
            className="p-4 rounded-lg transition-all duration-200 cursor-pointer"
            data-testid={dataTestId}
            style={{
                backgroundColor: 'var(--pallium-white)',
                border: overdue ? '1px solid var(--pallium-error-red)' : '1px solid var(--pallium-stroke-light)',
                boxShadow: 'var(--pallium-shadow-sm)',
                opacity: isUpdating ? 0.7 : 1
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                e.currentTarget.style.borderColor = 'var(--pallium-success-green)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-sm)';
                e.currentTarget.style.borderColor = overdue ? 'var(--pallium-error-red)' : 'var(--pallium-stroke-light)';
            }}
        >
            {/* Header: Icon + Action Item Name + Status */}
            <div className="flex items-start gap-3 mb-2">
                {/* Checkmark/Task Icon */}
                <div 
                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' }}
                >
                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm leading-tight" style={{ color: 'var(--pallium-text-primary)' }}>
                        {actionItem.name || 'Untitled Action Item'}
                    </h4>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* Status - Editable or Static */}
                        {editable && statusOptions.length > 0 ? (
                            <div className="relative editable-control" ref={statusRef}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all"
                                    style={{ 
                                        backgroundColor: statusStyle.bg, 
                                        color: statusStyle.color,
                                        border: '1px dashed currentColor'
                                    }}
                                    title="Click to change status"
                                >
                                    {actionItem.status || 'Set Status'}
                                    <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                {showStatusDropdown && (
                                    <div 
                                        className="absolute left-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[120px]"
                                        style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                                    >
                                        {statusOptions.map(opt => (
                                            <button
                                                key={opt}
                                                onClick={(e) => { e.stopPropagation(); handleStatusChange(opt); }}
                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                                style={{ 
                                                    color: opt === actionItem.status ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                                    fontWeight: opt === actionItem.status ? 600 : 400
                                                }}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : actionItem.status && (
                            <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                            >
                                {actionItem.status}
                            </span>
                        )}
                        {overdue && (
                            <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: 'rgba(252, 57, 57, 0.1)', color: 'var(--pallium-error-red)' }}
                            >
                                Overdue
                            </span>
                        )}
                    </div>
                </div>
                {/* Urgency Score */}
                {actionItem.score !== undefined && actionItem.score !== null && (
                    <div className="text-right flex-shrink-0">
                        <div className="text-xs flex items-center justify-end gap-0.5" style={{ color: 'var(--pallium-text-tertiary)' }}>
                            Urgency
                            <ScoreTooltip type="actionItemPriority" size="sm" />
                        </div>
                        <div className="text-sm font-bold" style={{ color: 'var(--pallium-warning-yellow)' }}>
                            {formatScore ? formatScore(actionItem.score) : (typeof actionItem.score === 'number' ? actionItem.score.toFixed(1) : actionItem.score)}
                        </div>
                    </div>
                )}
            </div>

            {/* Description snippet */}
            {actionItem.description && (
                <p className="text-xs mb-2 leading-relaxed" style={{ color: 'var(--pallium-text-secondary)' }}>
                    {truncateText(actionItem.description)}
                </p>
            )}

            {/* Initiative */}
            {actionItem.initiativeName && (
                <div className="text-xs mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                    <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Initiative:</span>
                    <span className="ml-1">{actionItem.initiativeName}</span>
                </div>
            )}

            {/* Footer: Assignee + Deadline */}
            <div className="text-xs pt-2 space-y-1.5" style={{ borderTop: '1px solid var(--pallium-stroke-lighter)' }}>
                {/* Assignee - Editable or Static */}
                {editable && assigneeOptions.length > 0 ? (
                    <div className="relative editable-control flex items-center gap-1" ref={assigneeRef}>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Assignee:</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowAssigneeDropdown(!showAssigneeDropdown); }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-all"
                            style={{ 
                                color: 'var(--pallium-text-secondary)',
                                border: '1px dashed var(--pallium-stroke-light)',
                                backgroundColor: 'var(--pallium-purple-0-5)'
                            }}
                            title="Click to change assignee"
                        >
                            {assigneeDisplayName || 'Unassigned'}
                            <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {showAssigneeDropdown && (
                            <div 
                                className="absolute left-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[180px] max-h-[200px] overflow-y-auto"
                                style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                            >
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleAssigneeChange(''); }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors italic"
                                    style={{ color: 'var(--pallium-mute-text)' }}
                                >
                                    Unassigned
                                </button>
                                {assigneeOptions.map(opt => (
                                    <button
                                        key={opt}
                                        onClick={(e) => { e.stopPropagation(); handleAssigneeChange(opt); }}
                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                        style={{ 
                                            color: opt === rawAssignee ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                            fontWeight: opt === rawAssignee ? 600 : 400
                                        }}
                                    >
                                        {getDriDisplayName(opt)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : rawAssignee && (
                    <div style={{ color: 'var(--pallium-text-secondary)' }}>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Assignee:</span>
                        <span className="ml-1">{assigneeDisplayName}</span>
                    </div>
                )}

                {/* Deadline - Editable or Static */}
                {editable ? (
                    <div className="editable-control flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="font-medium" style={{ color: overdue ? 'var(--pallium-error-red)' : 'var(--pallium-text-tertiary)' }}>Deadline:</span>
                        <SingleDatePicker
                            value={actionItem.deadline ? actionItem.deadline.split('T')[0] : ''}
                            onChange={(newDate) => {
                                if (onDeadlineChange) {
                                    setIsUpdating(true);
                                    onDeadlineChange(actionItem.id, newDate).finally(() => setIsUpdating(false));
                                }
                            }}
                            align="left"
                        />
                    </div>
                ) : actionItem.deadline && (
                    <div>
                        <span className="font-medium" style={{ color: overdue ? 'var(--pallium-error-red)' : 'var(--pallium-text-tertiary)' }}>
                            Deadline:
                        </span>
                        <span className="ml-1" style={{ color: overdue ? 'var(--pallium-error-red)' : 'var(--pallium-text-secondary)' }}>
                            {formatDate ? formatDate(actionItem.deadline) : actionItem.deadline}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActionItemCard;
